import type { ChainId, MarketId } from '@morpho-org/blue-sdk'
import type { Point, Rewards } from '@morpho-org/dapp-core'
import type { Address } from 'viem'

import { MathLib } from '@morpho-org/blue-sdk'
import { safeParseNumber } from '@morpho-org/blue-sdk-viem'

import type { VaultV2Exposure } from '@/lib/procedures/vaultV2Exposure.contract'

import { getMarketWithPoints, getVaultWithPoints } from '@/components/charts/helpers/getPoints'
import { getParsedValue } from '@/helpers/formatters'
import { computeRewards } from '@/helpers/rewards'

type ExposureMarket = VaultV2Exposure['markets'][number]
type ExposureVaultV1 = NonNullable<VaultV2Exposure['adapters'][number]['vaultV1']>
type ExposureReward = ExposureMarket['rewards'][number]

/** A market's APY paired with the assets allocated to it. */
export interface MarketAllocationForApy {
  apy: ReturnType<typeof getMarketV1Apy>
  allocation: bigint
}

const toWad = (fraction: number | null) => getParsedValue(fraction, safeParseNumber)

/** Positive reward APRs, one per reward token (campaigns paying the same token summed), highest first. */
function toRewards(rewards: ExposureReward[]): Rewards[] {
  const byToken = new Map<string, Rewards>()
  for (const reward of rewards.map(reward =>
    computeRewards({
      rewardToken: {
        address: reward.token.address,
        symbol: reward.token.symbol ?? '',
        decimals: 18,
        logoURI: reward.token.logoUri,
        chain: { id: reward.token.chainId }
      },
      apr: reward.supplyApr
    })
  )) {
    if (!reward.apr || reward.apr <= 0n) continue
    const key = reward.rewardToken.address.toLowerCase()
    const existing = byToken.get(key)
    byToken.set(key, existing ? { ...existing, apr: existing.apr! + reward.apr } : reward)
  }
  return [...byToken.values()].toSorted((a, b) => (a.apr === b.apr ? 0 : a.apr! > b.apr! ? -1 : 1))
}

export const getMetaMorphoVaultApy = ({
  chainId,
  vault,
  showPoints
}: {
  chainId: ChainId
  vault: ExposureVaultV1
  showPoints?: boolean
}) => {
  const points = showPoints
    ? getVaultWithPoints(
        chainId,
        vault.address,
        vault.allocations.map(allocation => ({
          supplyAssetsUsd: allocation.supplyAssetsUsd,
          market: {
            marketId: allocation.market.marketId as MarketId,
            collateralAsset: allocation.market.collateralToken && {
              address: allocation.market.collateralToken.address as Address
            }
          }
        }))
      )
    : []
  return {
    native: toWad(vault.netApyExcludingRewards) ?? 0n,
    net: toWad(vault.netApy) ?? 0n,
    performanceFee: toWad(vault.performanceFee) ?? 0n,
    rewards: toRewards(vault.rewards),
    points,
    variant: 'vault' as const
  }
}

export const getMarketV1Apy = ({
  chainId,
  market,
  showPoints
}: {
  chainId: ChainId
  market: ExposureMarket
  showPoints?: boolean
}) => {
  const { collateralToken, marketId } = market.market
  if (!collateralToken) return undefined

  const points = showPoints
    ? getMarketWithPoints(chainId, marketId as MarketId, collateralToken.address)
    : []
  return {
    native: toWad(market.supplyApy) ?? 0n,
    net: toWad(market.netSupplyApy) ?? 0n,
    rewards: toRewards(market.rewards),
    points,
    variant: 'market' as const
  }
}

// Helper functions

/**
 * Note: APY/APR values are stored as basis points (e.g., 500 = 5%)
 * Allocations are also in basis points (e.g., 2500 = 25% of total)
 */

interface RewardAggregation {
  totalWeightedApr: bigint
  asset: Rewards['rewardToken']
}

/**
 * Aggregates rewards by asset address, computing weighted APRs.
 *
 * Formula: For each reward asset, we compute:
 * weighted_apr = Σ(wMulDown(reward.apr, allocation)) for all markets with this reward
 *
 * Uses MathLib.wMulDown for precision-preserving multiplication with 18 decimal places.
 *
 * @param rewardMap - Map to accumulate weighted APRs by asset address
 * @param rewards - Rewards from a single market
 * @param allocation - Market allocation in basis points (e.g., 2500 = 25%)
 */
function aggregateRewards(
  rewardMap: Map<string, RewardAggregation>,
  rewards: Rewards[],
  allocation: bigint
) {
  for (const reward of rewards) {
    const assetId = reward.rewardToken.address

    if (!rewardMap.has(assetId)) {
      rewardMap.set(assetId, {
        totalWeightedApr: 0n,
        asset: reward.rewardToken
      })
    }

    const rewardData = rewardMap.get(assetId)!

    const weightedApr = reward.apr ? MathLib.wMulDown(reward.apr, allocation) : 0n

    rewardData.totalWeightedApr += weightedApr
  }
}

/**
 * Converts aggregated rewards map to array format with final weighted averages.
 *
 * Uses MathLib.wDivDown for precision-preserving division.
 *
 * Each reward is divided by the row's *total* allocation (across every market in
 * the row), not just the allocation of the markets that emit it. This is what
 * keeps the row's net consistent: `native + Σ(reward.apr)` then reproduces the
 * precomputed weighted net exactly, so `CellNetApy`'s `market`-variant recompute
 * (native + Σ rewards) matches the weighted `net`. Normalizing per-token instead
 * would overstate rewards that only some markets emit (a 50/50 row with a 10%
 * reward on one market would show +10% rather than the weighted +5%).
 *
 * @param rewardMap - Map with accumulated weighted APRs by asset address
 * @param totalAllocation - The row's total allocation across all markets
 */
function convertRewardMapToArray(
  rewardMap: Map<string, RewardAggregation>,
  totalAllocation: bigint
): Rewards[] {
  return Array.from(rewardMap.values()).map(({ totalWeightedApr, asset }) => ({
    rewardToken: asset,
    apr: totalAllocation > 0n ? MathLib.wDivDown(totalWeightedApr, totalAllocation) : 0n
  }))
}

/**
 * Calculates allocation-weighted average APY from multiple markets.
 *
 * For each metric (native APY, net APY, rewards APR), we compute:
 * weighted_average = wDivDown(Σ(wMulDown(value, allocation)), Σ(allocation))
 *
 * Uses MathLib.wMulDown and wDivDown for precision-preserving arithmetic with 18 decimal places.
 * This avoids precision loss from BigInt division truncation.
 *
 * @param markets - The markets' APYs (with their allocations) to weight across
 */
export function getWeightedMarketsApy(markets: MarketAllocationForApy[]) {
  if (markets.length === 0) return undefined

  let totalWeightedNativeApy = 0n
  let totalWeightedNetApy = 0n
  let totalAllocation = 0n
  const rewardMap = new Map<string, RewardAggregation>()
  const allPoints: Point[] = []

  for (const { apy, allocation } of markets) {
    if (!apy) continue

    totalAllocation += allocation

    // Weight APYs by allocation using wMulDown for precision
    totalWeightedNativeApy += MathLib.wMulDown(apy.native, allocation)
    totalWeightedNetApy += MathLib.wMulDown(apy.net, allocation)

    // Aggregate rewards by asset
    aggregateRewards(rewardMap, apy.rewards, allocation)

    // Collect all points
    allPoints.push(...apy.points)
  }

  if (totalAllocation === 0n) return undefined

  // Final weighted average using wDivDown for precision
  return {
    native: MathLib.wDivDown(totalWeightedNativeApy, totalAllocation),
    net: MathLib.wDivDown(totalWeightedNetApy, totalAllocation),
    rewards: convertRewardMapToArray(rewardMap, totalAllocation),
    points: allPoints,
    variant: 'market' as const
  }
}
